import React, { useCallback, useMemo } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';
import styled from 'styled-components';

import { ITEM_SIZES_BY_TYPE, VirtualizedListItem, VirtualizedSection } from './types';
import { buildVirtualizedListItems } from './utils';

// How many rows to buffer before they're rendered on-screen
const NUM_ROWS_PREBUFFERED = 10;

const Root = styled(AutoSizer)`
  max-height: 444px;
  overflow-y: scroll;
  margin-block-start: 18px;
`;

const List = styled(VariableSizeList)`
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-thumb {
    height: 160px;
    border-radius: 4px;
    background-color: #d9d9d9;
    margin-block-start: 18px;
  }
`;

type Props = {
  sections: VirtualizedSection[];
};

const VirtualizedLists: React.FC<Props> = ({ sections }: Props) => {
  // react-window expects one long list of items, rather than multiple sections, so we construct one from our ResultSections
  const listItems: VirtualizedListItem[] = useMemo(() => buildVirtualizedListItems(sections), [sections]);

  // react-window uses absolute positioning, so we need to use item height instead of css to manage spacing
  const getItemSize = useCallback(
    (index: number) => {
      const item = listItems[index];
      return ITEM_SIZES_BY_TYPE[item.type];
    },
    [listItems],
  );

  return (
    <Root>
      {({ width, height }) => (
        <List
          height={height}
          itemCount={listItems.length}
          itemSize={getItemSize}
          width={width}
          overscanCount={NUM_ROWS_PREBUFFERED}
        >
          {({ style, index }) => <div style={style}>{listItems[index].node}</div>}
        </List>
      )}
    </Root>
  );
};

export default VirtualizedLists;
